import React from "react";
import moment from "moment";
import { IconButton, withStyles } from "@material-ui/core";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import EditIcon from "@material-ui/icons/Create";
import { FormatListBulleted } from "@material-ui/icons";
import SwitchCustomizado from "features/shared/components/SwitchCustomizado";
import TabelaProdap from "features/shared/components/TabelaProdap";
import { t } from "adapters/i18n";
import IngredientRegisterHistoricModal from "../../ingredientRegisterHistoricModal/components/ingredientRegisterHistoricModal";

const columnCodeProduct = ({ classes }) => ({
  id: "productName",
  titulo: (
    <span style={{ display: "block", maxWidth: 118 }}>
      {t("ingredients:ingredient-code")}
    </span>
  ),
  pegaValor: (linha) =>
    linha.Produto ? (
      <span>
        <span>{linha.Produto.name}</span>
        <span className={classes.subtitle}>{linha.Produto.code}</span>
      </span>
    ) : (
      <span>-</span>
    ),
  propriedadesCustomizadas: {
    "data-testid": "codigoPAIngredientes",
  },
});

const columnNomeMateriaPrima = () => ({
  id: "NomeMateriaPrima",
  titulo: t("ingredients:raw-material").toUpperCase(),
  pegaValor: (linha) => <span>{linha.NomeMateriaPrima || "-"}</span>,
  propriedadesCustomizadas: {
    "data-testid": "nomeIngredientes",
  },
});

const columnDescricao = () => ({
  id: "Descricao",
  titulo: t("ingredients:ingredient-name"),
  infoMessage: t("ingredients:ingredient-name-description"),
  pegaValor: (linha) => <span>{linha.Descricao}</span>,
  propriedadesCustomizadas: {
    "data-testid": "descricaoIngredientes",
  },
});

const columnDataCompra = () => ({
  id: "DataCompra",
  titulo: (
    <span style={{ maxWidth: 80, display: "block" }}>
      {t("ingredients:purchase-date")}
    </span>
  ),
  sortMethod: "date",
  pegaValor: (linha) => (
    <span>
      {linha.DataCompra ? moment(linha.DataCompra).format("DD/MM/YYYY") : "-"}
    </span>
  ),
  propriedadesCustomizadas: {
    "data-testid": "dataIngredientes",
  },
});

const columnCustoPorQuilo = () => ({
  id: "CustoPorQuilo",
  titulo: (
    <div style={{ display: "grid", placeItems: "center" }}>
      <span style={{ maxWidth: 68, alignSelf: "center" }}>
        {t("ingredients:cost-per-kg")}
      </span>
    </div>
  ),
  pegaValor: (linha) => (
    <span>
      {linha.CustoPorQuilo ? `R$${linha.CustoPorQuilo.toFixed(2)}` : "-"}
    </span>
  ),
  propriedadesCustomizadas: {
    "data-testid": "precoIngredientes",
  },
});

const columnLancaCompra = ({ classes, lancarCompra }) => ({
  id: "",
  ordenavel: false,
  titulo: (
    <div style={{ display: "grid", placeItems: "center" }}>
      <span
        className={classes.divAlinhamento}
        style={{ maxWidth: 68, alignSelf: "center" }}
      >
        {t("ingredients:make-a-purchase")}
      </span>
    </div>
  ),
  headerCellClass: classes.middleCellClass,
  cellClass: classes.middleCellClass,
  pegaValor: (linha) => (
    <div className={classes.divAlinhamento}>
      <IconButton
        data-testid="botaoLancarCompra"
        onClick={() => lancarCompra(linha)}
        aria-label={t("ingredients:make-a-purchase")}
      >
        <MonetizationOnIcon />
      </IconButton>
    </div>
  ),
  propriedadesCustomizadas: {
    "data-testid": "lancarCompra",
  },
});

const columnEditar = ({ classes, ativarEdicaoIngrediente }) => ({
  id: "",
  titulo: (
    <div className={classes.divAlinhamento}>
      {t("common:actions.edit").toUpperCase()}
    </div>
  ),
  ordenavel: false,
  headerCellClass: classes.middleCellClass,
  cellClass: classes.middleCellClass,
  pegaValor: (linha) => (
    <div className={classes.divAlinhamento}>
      <IconButton
        data-testid="botaoEditarIngredientes"
        onClick={() => ativarEdicaoIngrediente(linha)}
        aria-label="Edit"
      >
        <EditIcon />
      </IconButton>
    </div>
  ),
  propriedadesCustomizadas: {
    "data-testid": "editarIngredientes",
  },
});

const columnAtivo = ({ classes, ativaOuDesativaIngrediente }) => ({
  id: "Ativo",
  titulo: t("common:status.active").toUpperCase(),
  estilo: { textAlign: "center" },
  headerCellClass: classes.middleCellClassWithOrder,
  cellClass: classes.middleCellClass,
  pegaValor: (linha) => (
    <div className={classes.divAlinhamento}>
      <SwitchCustomizado
        testId={`botaoAtivaDesativa-${linha.IdIngrediente}`}
        handleClick={() =>
          ativaOuDesativaIngrediente(
            linha.IdIngrediente,
            linha.Ativo,
            linha.Descricao
          )
        }
        checked={linha.Ativo}
      />
    </div>
  ),
  propriedadesCustomizadas: {
    "data-testid": "switchIngredientes",
  },
});

const columnHistorico = ({ classes, abreHistoricoIngredientes }) => ({
  ordenavel: false,
  titulo: (
    <div className={classes.divAlinhamento}>
      {t("ingredients:history").toUpperCase()}
    </div>
  ),
  headerCellClass: classes.middleCellClass,
  cellClass: classes.middleCellClass,
  pegaValor: (linha) => (
    <div className={classes.divAlinhamento}>
      <IconButton
        data-testid="botaoDetalhesIngredientes"
        aria-label="detalhes"
        onClick={() => abreHistoricoIngredientes(linha)}
      >
        <FormatListBulleted className={classes.iconeMenu} />
      </IconButton>
    </div>
  ),
  propriedadesCustomizadas: {
    "data-testid": "detalhesIngredientes",
  },
});

const pegaColunas = ({
  classes,
  lancarCompra,
  ativarEdicaoIngrediente,
  ativaOuDesativaIngrediente,
  abreHistoricoIngredientes,
  isInternalUser,
}) => {
  const communColumns = [
    columnNomeMateriaPrima({ classes }),
    columnDescricao({ classes }),
    columnDataCompra({ classes }),
    columnCustoPorQuilo({ classes }),
    columnLancaCompra({ classes, lancarCompra }),
    columnEditar({ classes, ativarEdicaoIngrediente }),
    columnAtivo({ classes, ativaOuDesativaIngrediente }),
    columnHistorico({ classes, abreHistoricoIngredientes }),
  ];
  if (isInternalUser) {
    return [columnCodeProduct({ classes }), ...communColumns];
  } else {
    return communColumns;
  }
};

export const IngredientRegisterTable = ({
  historico,
  ingredientes,
  abrirHistorico,
  ingredienteSelecionado,
  abreHistoricoIngredientes,
  obterHistorico,
  setAbrirHistorico,
  lancarCompra,
  ativarEdicaoIngrediente,
  ativaOuDesativaIngrediente,
  classes,
  isInternalUser,
}) => {
  return (
    <div className={classes.wrapperTable}>
      <TabelaProdap
        tabelaEsquerda
        modoOrdenacao={"CLIENTE"}
        linhas={ingredientes}
        ativarPaginacao={false}
        ingredientesHeaderCelulas
        propriedadesLinhas={{
          "data-testid": "linhasTabelaIngredientes",
        }}
        propriedadesCelulasCabecalho={{
          "data-testid": "ingredientesHeaderCelulas",
        }}
        colunas={pegaColunas({
          classes,
          lancarCompra,
          ativarEdicaoIngrediente,
          ativaOuDesativaIngrediente,
          abreHistoricoIngredientes,
          isInternalUser,
        })}
      />

      <IngredientRegisterHistoricModal
        ingrediente={ingredienteSelecionado}
        abrirHistorico={abrirHistorico}
        aoFechar={() => setAbrirHistorico(false)}
        historico={historico.items}
        obterHistorico={obterHistorico}
        isInternalUser={isInternalUser}
      />
    </div>
  );
};

const styles = (theme) => ({
  divAlinhamento: {
    display: "block",
    textAlign: "center",
  },
  subtitle: {
    fontWeight: "500",
    display: "block",
  },
  iconeMenu: {
    color: theme.palette.primary.main,
  },
  middleCellClass: {
    padding: "4px 24px 4px 24px",
  },
  middleCellClassWithOrder: {
    padding: "4px 28px 4px 42px",
  },
});

export default withStyles(styles)(IngredientRegisterTable);
